'use client'

import { OutputData } from '@editorjs/editorjs'
import { useEffect, useMemo, useRef } from 'react'

import ZSectionAssignedEmployees from '@/_components/sections/employees/z-section-assigned-employees'
import { ZEditorJsRenderer } from '@/_components/universal/editorjs-renderer'
import {
	IAmbulance,
	IPriceListGroup,
	IPriceListItem,
	ITemplate,
	ITemplateTimeslot,
	PRICE_LIST_ITEM_TYPE_ENUM
} from '@/_types'
import { buildGa4CartItem, pushViewItemListOnce } from '@/_utils/analytics/datalayer'
import { saveGa4LastAmbulanceSlug } from '@/_utils/analytics/ga4-ambulance-slug.storage'

import ZAmbulanceContentSidebar from '../../components/content/z-ambulance-content-sidebar'
import { hasValidContentBlocks } from '../utils/content.utils'

import AAmbulanceContentImages from './content/a-ambulance-content-images'
import AAmbulanceContentItem from './content/a-ambulance-content-item'
import AAmbulanceContentPriceGroups from './content/a-ambulance-content-price-groups'
import AAmbulanceContentTimeslots from './content/a-ambulance-content-timeslots'
import AAmbulancePriceList from './price-list/a-ambulance-price-list'
import AAmbulanceServices from './services/a-ambulance-services'

/** Translation strings for pages.ambulance_detail, passed from Server Component to avoid NextIntlClientProvider context. */
export interface IAmbulanceDetailTranslations {
	sidebar: {
		aboutService: string
		aboutAmbulance: string
		ambulanceTeamDescription: string
		packages: string
		priceList: string
	}
	aboutService: string
	aboutAmbulance: string
	examinationProcess: string
	benefitsForPatients: string
}

interface IProps {
	ambulance: IAmbulance
	closestTimeslots?: ITemplateTimeslot[] | null
	translations: IAmbulanceDetailTranslations
}

export default function AAmbulanceContent({
	ambulance,
	closestTimeslots,
	translations: t
}: IProps) {
	const viewItemListFiredRef = useRef(false)

	// TODO: Remove after API is fixed
	const priceListGroups = useMemo(() => {
		if (ambulance?.priceListGroups === null || ambulance?.priceListGroups === undefined) {
			return []
		}

		if (Array.isArray(ambulance.priceListGroups)) {
			return ambulance.priceListGroups
		}

		if (typeof ambulance.priceListGroups === 'object') {
			return Object.values(ambulance.priceListGroups) as IPriceListGroup[]
		}

		return []
	}, [ambulance.priceListGroups])

	// TODO: Remove after API is fixed
	const priceListItems = useMemo(() => {
		if (ambulance?.priceListItems === null || ambulance?.priceListItems === undefined) {
			return []
		}

		if (Array.isArray(ambulance.priceListItems)) {
			return ambulance.priceListItems
		}

		if (typeof ambulance.priceListItems === 'object') {
			return Object.values(ambulance.priceListItems) as IPriceListItem[]
		}

		return []
	}, [ambulance.priceListItems])

	const packagePriceListItems = useMemo(() => {
		return priceListItems.filter(item => item.type === PRICE_LIST_ITEM_TYPE_ENUM.PACKAGE)
	}, [priceListItems])

	const servicePriceListItems = useMemo(() => {
		return priceListItems.filter(item => item.type === PRICE_LIST_ITEM_TYPE_ENUM.SERVICE)
	}, [priceListItems])

	const templatesFromTimeslots = useMemo((): ITemplate[] => {
		if (!closestTimeslots?.length) return []
		const seen = new Set<number>()
		const templates: ITemplate[] = []
		for (const slot of closestTimeslots) {
			const template = slot.template
			if (!template || seen.has(template.id)) continue
			seen.add(template.id)
			templates.push(template)
		}
		return templates
	}, [closestTimeslots])

	useEffect(() => {
		saveGa4LastAmbulanceSlug(ambulance.slug, ambulance.name)
	}, [ambulance.slug, ambulance.name])

	useEffect(() => {
		if (viewItemListFiredRef.current) return
		if (templatesFromTimeslots.length === 0 && priceListItems.length === 0) return

		const itemListId = `ambulancia-${ambulance.slug}`
		const itemListName = `Ambulancia - ${ambulance.name}`

		const templateItems = templatesFromTimeslots.map((template, index) =>
			buildGa4CartItem(
				{
					type: 'template',
					templateId: template.id,
					name: template.name,
					price: template.price ?? 0
				},
				{ item_list_id: itemListId, item_list_name: itemListName, index, item_category2: ambulance.name }
			)
		)

		const priceItems = priceListItems.map((item, index) =>
			buildGa4CartItem(
				{
					type: 'price-list-item',
					itemId: item.id,
					name: item.name,
					price: item.price ?? 0,
					priceOld: item.priceOld ?? undefined
				},
				{
					item_list_id: itemListId,
					item_list_name: itemListName,
					index: templatesFromTimeslots.length + index,
					item_category2: ambulance.name
				}
			)
		)

		const items = [...templateItems, ...priceItems]
		const pushed = pushViewItemListOnce(itemListId, {
			item_list_id: itemListId,
			item_list_name: itemListName,
			items
		})
		if (pushed) viewItemListFiredRef.current = true
	}, [templatesFromTimeslots, priceListItems, ambulance.slug, ambulance.name])

	const aboutServiceHasValidContentBlocks = hasValidContentBlocks(ambulance.aboutService)
	const aboutAboutHasValidContentBlocks = hasValidContentBlocks(ambulance.aboutAbout)

	const sidebarItems = useMemo(() => {
		let firstItem

		if (aboutServiceHasValidContentBlocks) {
			firstItem = { href: '#o-sluzbe', label: t.sidebar.aboutService }
		} else {
			firstItem = { href: '#o-ambulancii', label: t.sidebar.aboutAmbulance }
		}

		return [
			firstItem,
			{ href: '#medante-odbornici', label: t.sidebar.ambulanceTeamDescription },
			...(!!packagePriceListItems.length
				? [
						{
							href: '#baliky-nadstandardnych-sluzieb',
							label: t.sidebar.packages
						}
					]
				: []),
			...(!!servicePriceListItems.length
				? [{ href: '#cennik', label: t.sidebar.priceList }]
				: [])
		]
	}, [t, packagePriceListItems, servicePriceListItems, aboutServiceHasValidContentBlocks])

	return (
		<div className='box_amb'>
			<ZAmbulanceContentSidebar sidebarItems={sidebarItems} />

			<section
				id='o-ambulancii'
				className='box_detail-amb-content--new pdt-20md pdt-70lg pdt-100'
			>
				<div className='wrapper_full'>
					<div className='wrapper_amb wrapper_amb--big'>
						<div className='accord mgt-30 mgt-0lg  pdb-10lg'>
							{aboutServiceHasValidContentBlocks ? (
								<AAmbulanceContentItem
									title={t.aboutService}
									content={ambulance.aboutService}
									defaultExpanded={true}
								/>
							) : (
								aboutAboutHasValidContentBlocks && (
									<AAmbulanceContentItem
										title={t.aboutAmbulance}
										content={ambulance.aboutAbout}
										defaultExpanded={true}
									/>
								)
							)}
							<AAmbulanceContentTimeslots
								ambulanceSlug={ambulance.slug}
								ambulanceName={ambulance.name}
								closestTimeslots={
									Array.isArray(closestTimeslots) ? closestTimeslots : null
								}
							/>

							<AAmbulanceContentItem
								title={t.examinationProcess}
								content={ambulance.aboutExaminationProcess}
							/>

							<AAmbulanceContentItem
								title={t.benefitsForPatients}
								content={ambulance.aboutBenefits}
							/>

							<AAmbulanceContentPriceGroups
								ambulance={ambulance}
								priceGroups={priceListGroups}
							/>
						</div>

						{hasValidContentBlocks(ambulance.aboutContent) && (
							<div className='section_head mgt-30md mgt-80lg mgt-90 mgl-0lg mgl-120'>
								<div className='anim_delay anim_drop1 observe'>
									<ZEditorJsRenderer
										data={ambulance.aboutContent as OutputData}
										className='!p-0'
									/>
								</div>
							</div>
						)}
					</div>
				</div>

				<AAmbulanceContentImages
					images={Array.isArray(ambulance.contentImages) ? ambulance.contentImages : []}
				/>
			</section>

			<ZSectionAssignedEmployees
				employees={ambulance.employees}
				orderUrl={`/volne-terminy?ambulance=${ambulance.slug}`}
			/>
			<AAmbulanceServices
				ambulance={ambulance}
				priceListItems={packagePriceListItems}
				allPriceListItems={priceListItems}
			/>
			<AAmbulancePriceList
				ambulance={ambulance}
				priceListItems={servicePriceListItems}
				allPriceListItems={priceListItems}
			/>
		</div>
	)
}
